import React from 'react';
import { Box, Typography, Container, Chip, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { Users, Package, Leaf, CheckCircle2 } from 'lucide-react';
import { images } from '../assets/images';
import { gradients } from '../theme';
import FadeIn from './FadeIn';
import Counter from './Counter';

const About: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: 'black',
        py: 12,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          height: '60%',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.04) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(100px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <FadeIn direction="up">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#00f2ff', fontWeight: 600, letterSpacing: 2, mb: 2, display: 'block' }}
            >
              WHO WE ARE
            </Typography>
            <Typography
              variant="h2"
              sx={{
                ...gradients.primaryText,
                mb: 3,
                fontWeight: 700,
              }}
            >
              About Bluecat Recycling
            </Typography>
            <Typography
              sx={{
                color: 'grey.400',
                fontSize: '1.25rem',
                maxWidth: 900,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Bluecat Recycling is a UAE-based catalytic converter recycling and precious-metal recovery company. We help workshops, scrap dealers and fleet operators convert used catalytic converters into real financial returns through accurate PGM analysis and transparent, market-linked pricing.
            </Typography>
          </Box>
        </FadeIn>

        {/* Stats Cards */}
        <Grid container spacing={3} justifyContent="center">
          {/* Image Card */}
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <FadeIn delay={0.3} fullWidth>
              <Paper
                component={motion.div}
                whileHover={{ y: -8 }}
                sx={{
                  height: 360,
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#00f2ff',
                    boxShadow: '0 20px 40px -10px rgba(0, 242, 255, 0.2)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={images.aboutImage}
                  alt="About"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Paper>
            </FadeIn>
          </Grid>

          {/* Partners Card */}
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <FadeIn delay={0.4} fullWidth>
              <Paper
                component={motion.div}
                whileHover={{ y: -8, borderColor: '#00f2ff' }}
                sx={{
                  height: 360,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0, 242, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0, 242, 255, 0.2)',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 20px 40px -10px rgba(0, 242, 255, 0.3)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0, 242, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#00f2ff',
                    mb: 2,
                  }}
                >
                  <Users size={28} />
                </Box>
                <Typography variant="h4" sx={{ color: 'white', mb: 1.5, fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Counter value={120} />+
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'grey.400', mb: 1.5, fontSize: '0.9rem', lineHeight: 1.5 }}
                >
                  Workshops, scrap dealers & fleet operators partnered with Bluecat to sell used catalytic converters through a compliant and transparent recovery system.
                </Typography>
                <Box sx={{ borderTop: '1px solid rgba(0, 242, 255, 0.2)', pt: 1.5, mt: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <CheckCircle2 size={18} color="#00f2ff" />
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                      Reliable catalyst pickup
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle2 size={18} color="#00f2ff" />
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                      Secure & compliant handling
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </FadeIn>
          </Grid>

          {/* Processed Card (Green) */}
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <FadeIn delay={0.5} fullWidth>
              <Paper
                component={motion.div}
                whileHover={{ y: -8, borderColor: '#66bb6a' }}
                sx={{
                  height: 360,
                  borderRadius: 4,
                  backgroundColor: 'rgba(102, 187, 106, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(102, 187, 106, 0.3)',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 20px 40px -10px rgba(102, 187, 106, 0.4)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(102, 187, 106, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#66bb6a',
                    mb: 2,
                  }}
                >
                  <Package size={28} />
                </Box>
                <Typography variant="h4" sx={{ color: 'white', mb: 1.5, fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Counter value={500} />+
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'grey.300', mb: 1.5, fontSize: '0.9rem', lineHeight: 1.5 }}
                >
                  Catalytic converters processed every month through decanning, XRF testing and precious-metal recovery to extract Platinum, Palladium & Rhodium.
                </Typography>
                <Box sx={{ borderTop: '1px solid rgba(102, 187, 106, 0.3)', pt: 1.5, mt: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <CheckCircle2 size={18} color="#66bb6a" />
                    <Typography variant="caption" sx={{ color: 'grey.300' }}>
                      Lab-grade metal analysis
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle2 size={18} color="#66bb6a" />
                    <Typography variant="caption" sx={{ color: 'grey.300' }}>
                      Accurate PGM content
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </FadeIn>
          </Grid>

          {/* Projects Card */}
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <FadeIn delay={0.6} fullWidth>
              <Paper
                component={motion.div}
                whileHover={{ y: -8, borderColor: '#00f2ff' }}
                sx={{
                  height: 360,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0, 242, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0, 242, 255, 0.2)',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 20px 40px -10px rgba(0, 242, 255, 0.3)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0, 242, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#00f2ff',
                    mb: 2,
                  }}
                >
                  <Leaf size={28} />
                </Box>
                <Typography variant="h4" sx={{ color: 'white', mb: 1.5, fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Counter value={200} />+
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'grey.400', mb: 2, fontSize: '0.9rem', lineHeight: 1.5 }}
                >
                  PGM recovery and sustainability projects delivered across the UAE, helping reduce mining demand and automotive waste through metal recycling.
                </Typography>

                {/* Tags */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 'auto',
                  }}
                >
                  <Chip
                    label="GOGREEN"
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: 10,
                      borderColor: 'rgba(0, 242, 255, 0.5)',
                      color: '#00f2ff',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label="RECYCLING"
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: 10,
                      borderColor: 'rgba(0, 242, 255, 0.5)',
                      color: '#00f2ff',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label="ECOFRIENDLY"
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: 10,
                      borderColor: 'rgba(0, 242, 255, 0.5)',
                      color: '#00f2ff',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Paper>
            </FadeIn>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
